import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { Alert } from '@ferro/ui';
import { VaxInsightCard } from '../components/reports/VaxInsightCard.js';
import { formatDate } from '../components/dog-profile/format.js';
import {
  fetchComplianceRoster,
  readSession,
  type ComplianceCounts,
  type ComplianceFilter,
  type ComplianceRow,
  type ComplianceStatus,
} from '../lib/api.js';
import { rolesCanAccess, ROUTE_ACCESS_RULES } from '../lib/route-permissions.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
// Sawyer 09 pet-inactivity (D1)
import { InactiveDogsToggle } from '../components/reports/InactiveDogsToggle.js';
import { inactiveSinceLabel } from '../components/reports/inactive-since.js';

type ComplianceProps = {
  config: ClientConfig;
};

type Payload = {
  rows: ComplianceRow[];
  counts: ComplianceCounts;
  days: number;
  // Sawyer 09 pet-inactivity (D1) — absent on an older server.
  inactivityMonths: number | null;
  inactiveDogsHidden: number;
  truncated: boolean;
};

const FILTERS: ReadonlyArray<{
  value: ComplianceFilter;
  label: string;
  countKey: keyof ComplianceCounts;
}> = [
  { value: 'all', label: 'Needs attention', countKey: 'all' },
  { value: 'expired', label: 'Expired', countKey: 'expired' },
  { value: 'expiring', label: 'Expiring soon', countKey: 'expiring' },
  { value: 'unverified', label: 'Unverified', countKey: 'unverified' },
];

/**
 * Ted #32a (E1.4, #1156) — an expiry is a calendar day (`@db.Date`, stored as
 * UTC midnight). `toLocaleDateString` in the browser's zone read 2026-10-21
 * as Oct 20 for every desk in Denver. The shared formatter reads the stored
 * calendar day, so the date shown is the date on the certificate.
 */
function expiryLabel(iso: string | null): string {
  return formatDate(iso) ?? '—';
}

function prettyVaccineName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusBadgeClass(status: ComplianceStatus): string {
  return `ferro-compliance__badge ferro-compliance__badge--${status}`;
}

function statusLabel(status: ComplianceStatus): string {
  switch (status) {
    case 'expired':
      return 'Expired';
    case 'expiring':
      return 'Expiring';
    case 'unverified':
      return 'Unverified';
    case 'current':
      return 'Current';
  }
}

export function Compliance({ config }: ComplianceProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { staff } = readSession();
  // Spec D3 (2026-07-09 wave, Johnny's locked #221 decision): the
  // VaxInsightCard moved here from the dashboard. It deep-links into
  // /reports?tab=vax, so it renders only for roles that can OPEN
  // /reports (owner/manager) — a receptionist already has the full
  // roster below and must not get a card whose tap dead-ends in a 403.
  // #428 — union over primary + granted roles.
  const canSeeReports = rolesCanAccess(
    effectiveRolesOf(staff),
    ROUTE_ACCESS_RULES['/reports'],
  );
  const [filter, setFilter] = useState<ComplianceFilter>('all');
  // Sawyer 09 pet-inactivity (D1) — `?inactive=1` is deep-linkable and survives a reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const includeInactive = searchParams.get('inactive') === '1';
  const setIncludeInactive = useCallback(
    (next: boolean) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set('inactive', '1');
      else params.delete('inactive');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  // Stale-while-revalidate: keep the last successful payload while a new
  // filter loads, so the (filter-independent) pill counts AND the table
  // don't blank on every pill click — `isFetching` drives the busy state.
  const [data, setData] = useState<Payload | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (currentFilter: ComplianceFilter, currentIncludeInactive: boolean) => {
      const { token } = readSession();
      if (!token) {
        setError('Not authenticated');
        setIsFetching(false);
        return;
      }
      setIsFetching(true);
      setError(null);
      try {
        const res = await fetchComplianceRoster(apiUrl, token, {
          status: currentFilter,
          includeInactive: currentIncludeInactive,
        });
        setData({
          rows: res.rows,
          counts: res.counts,
          days: res.days,
          inactivityMonths: res.inactivity_months ?? null,
          inactiveDogsHidden: res.inactive_dogs_hidden ?? 0,
          truncated: res.truncated === true,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load compliance roster',
        );
      } finally {
        setIsFetching(false);
      }
    },
    [apiUrl],
  );

  useEffect(() => {
    void load(filter, includeInactive);
  }, [filter, includeInactive, load]);

  const counts = data?.counts ?? null;
  const days = data?.days ?? 30;
  const rows = data?.rows ?? [];
  const hiddenCount = data?.inactiveDogsHidden ?? 0;

  // Error wins (surfaces the failure + retry). Otherwise: an initial load
  // with nothing yet shows the skeleton; once we have a payload, the table
  // stays put across filter refetches.
  const showError = error !== null;
  const showInitialLoading = !showError && isFetching && data === null;
  const showEmpty = !showError && !showInitialLoading && rows.length === 0;
  const showTable = !showError && !showInitialLoading && rows.length > 0;

  return (
    <section
      className="ferro-admin-page ferro-compliance"
      aria-busy={isFetching}
    >
      <header className="ferro-detail__header">
        <h1 className="ferro-section__title">Vaccination compliance</h1>
        <p className="ferro-detail__meta">
          Every dog whose vaccinations are expired, expiring within {days} days,
          or unverified. Click a row to open the customer.
        </p>
        {/* Sawyer 09 pet-inactivity (D1) — nothing renders when the rule is off or the server is older. */}
        <InactiveDogsToggle
          idPrefix="compliance"
          months={data?.inactivityMonths}
          hiddenCount={hiddenCount}
          includeInactive={includeInactive}
          onChange={setIncludeInactive}
          busy={isFetching}
        />
      </header>

      {data?.truncated ? (
        <Alert variant="warning">
          <span data-testid="compliance-truncated">
            Showing the first 20,000 vaccination records. Some dogs are not on this list.
          </span>
        </Alert>
      ) : null}

      {canSeeReports ? (
        <div className="ferro-dashboard-insight-row">
          <VaxInsightCard apiUrl={apiUrl} />
        </div>
      ) : null}

      <div
        className="ferro-compliance__filters"
        role="group"
        aria-label="Compliance status filter"
      >
        {FILTERS.map((f) => {
          const active = filter === f.value;
          const count = counts ? counts[f.countKey] : null;
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={active}
              data-filter={f.value}
              className={`ferro-compliance__filter${
                active ? ' ferro-compliance__filter--active' : ''
              }`}
              onClick={() => setFilter(f.value)}
            >
              <span className="ferro-compliance__filter-label">{f.label}</span>
              {count !== null ? (
                <span className="ferro-compliance__filter-count">{count}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {showInitialLoading ? (
        <p className="ferro-muted" role="status">
          Loading compliance roster…
        </p>
      ) : null}

      {showError ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load(filter, includeInactive)}
          >
            Retry
          </button>
        </div>
      ) : null}

      {showEmpty ? (
        <article className="ferro-dashboard__card" aria-label="Empty state">
          {!includeInactive && hiddenCount > 0 ? (
            // Sawyer 09 pet-inactivity (D1, AC12) — never "all clean" when dogs are hidden.
            <p className="ferro-muted" data-testid="compliance-empty-hidden">
              {filter === 'all' ? 'No active dogs need attention.' : 'No active dogs match this filter.'}{' '}
              {hiddenCount} inactive {hiddenCount === 1 ? 'dog' : 'dogs'} hidden.
            </p>
          ) : (
            <p className="ferro-muted">
              No dogs match this filter. Vaccination records are{' '}
              {filter === 'all' ? 'up to date' : 'clean for this status'}.
            </p>
          )}
        </article>
      ) : null}

      {showTable ? (
        <article className="ferro-dashboard__card" aria-label="Compliance roster">
          <table className="ferro-compliance__table" aria-label="Compliance roster">
            <thead>
              <tr>
                <th scope="col">Customer</th>
                <th scope="col">Dog</th>
                <th scope="col">Vaccine</th>
                <th scope="col">Expires</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.vaccination_id}
                  className={`ferro-compliance__row ferro-compliance__row--${row.status}`}
                  data-status={row.status}
                  data-customer-id={row.customer_id}
                  data-dog-id={row.dog_id}
                >
                  <td data-label="Customer">
                    <Link
                      to={`/customers/${row.customer_id}`}
                      className="ferro-compliance__customer-link"
                    >
                      {row.customer_name}
                    </Link>
                    {row.customer_email ? (
                      <div className="ferro-compliance__customer-meta">
                        {row.customer_email}
                      </div>
                    ) : null}
                  </td>
                  <td data-label="Dog">
                    {row.dog_name}
                    {inactiveSinceLabel(row.dog_inactive_since) ? (
                      <span className="ferro-inactive-badge" data-testid="compliance-inactive-badge">
                        {inactiveSinceLabel(row.dog_inactive_since)}
                      </span>
                    ) : null}
                  </td>
                  <td data-label="Vaccine">{prettyVaccineName(row.vaccine_name)}</td>
                  <td data-label="Expires">{expiryLabel(row.expires_at)}</td>
                  <td data-label="Status">
                    <span className={statusBadgeClass(row.status)}>
                      {statusLabel(row.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      ) : null}
    </section>
  );
}
